import {
  CaretRight,
  Lightning,
  PencilSimple,
  ShieldCheck,
  X,
} from '@phosphor-icons/react';
import { createPortal } from 'react-dom';
import { LinkMethod } from './types';

interface LinkMethodChooserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMethod: (method: LinkMethod) => void;
  plaidReady: boolean;
}

export const LinkMethodChooserModal = ({
  isOpen,
  onClose,
  onSelectMethod,
  plaidReady,
}: LinkMethodChooserModalProps) => {
  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[480px] overflow-hidden rounded-[4px] bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-[1px] border-bordergray px-6 py-4">
          <h2 className="text-[16px] font-semibold text-primary">
            Link a bank account
          </h2>
          <X
            className="cursor-pointer text-graysecondary hover:text-primary"
            size={22}
            weight="regular"
            onClick={onClose}
          />
        </div>

        <div className="flex flex-col gap-5 px-6 py-6">
          <p className="text-[14px] leading-[20px] text-graysecondary">
            Choose how you'd like to connect your account. Both methods are
            secure.
          </p>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => onSelectMethod(LinkMethod.Plaid)}
              disabled={!plaidReady}
              className="group flex w-full flex-row items-center gap-4 rounded-[4px] border-[1px] border-bordergray bg-white p-4 text-left transition hover:border-accent hover:bg-bgyellow focus:outline-none focus:ring-2 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-bordergray disabled:hover:bg-white"
            >
              <Lightning
                size={26}
                weight="duotone"
                className="shrink-0 text-primary"
              />
              <div className="flex-1">
                <p className="text-[15px] font-semibold text-primary">
                  Continue with Plaid
                </p>
                <p className="mt-1 text-[13px] leading-[18px] text-graysecondary">
                  {plaidReady
                    ? 'Sign in to your bank in seconds. Most major banks supported.'
                    : 'Getting Plaid ready, please wait a moment.'}
                </p>
              </div>
              <CaretRight
                size={20}
                className="shrink-0 text-graysecondary group-hover:text-primary"
              />
            </button>

            <button
              type="button"
              onClick={() => onSelectMethod(LinkMethod.Manual)}
              className="group flex w-full flex-row items-center gap-4 rounded-[4px] border-[1px] border-bordergray bg-white p-4 text-left transition hover:border-accent hover:bg-bgyellow focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <PencilSimple
                size={26}
                weight="duotone"
                className="shrink-0 text-primary"
              />
              <div className="flex-1">
                <p className="text-[15px] font-semibold text-primary">
                  Link manually
                </p>
                <p className="mt-1 text-[13px] leading-[18px] text-graysecondary">
                  Enter your routing and account number to add your bank
                  yourself.
                </p>
              </div>
              <CaretRight
                size={20}
                className="shrink-0 text-graysecondary group-hover:text-primary"
              />
            </button>
          </div>

          <div className="flex items-start gap-2 rounded-[4px] bg-primarybg px-3 py-2.5">
            <ShieldCheck
              size={18}
              weight="duotone"
              className="mt-0.5 shrink-0 text-secondary"
            />
            <p className="text-[12px] leading-[18px] text-graysecondary">
              Your information is encrypted and only used to enable ACH
              transfers to and from your TACH account.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
